import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Loader2, AlertCircle } from 'lucide-react';
import { EvaluationTestSummary, SystemPromptSummary } from '../types/evaluation';
import { ModelInfo } from '../types/models';
import { EvaluationApiService } from '../services/evaluationApi';
import { AppLayout } from './AppLayout';
import { JudgeModelSelector } from './JudgeModelSelector';

function generateDefaultName(): string {
  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  const dateStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  return `Scoring Validation - ${dateStr}`;
}

export const CreateScoringValidationPage = () => {
  const navigate = useNavigate();
  const evaluationApi = EvaluationApiService.getInstance();

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Data
  const [sourceTests, setSourceTests] = useState<EvaluationTestSummary[]>([]);
  const [systemPrompts, setSystemPrompts] = useState<SystemPromptSummary[]>([]);

  // Form state
  const [runName, setRunName] = useState('');
  const [description, setDescription] = useState('');
  const [sourceTestId, setSourceTestId] = useState('');
  const [systemPromptHash, setSystemPromptHash] = useState('');
  const [judgeModelProvider, setJudgeModelProvider] = useState<string>('');
  const [judgeModelId, setJudgeModelId] = useState('');
  const [judgeModelInfo, setJudgeModelInfo] = useState<ModelInfo | undefined>(undefined);
  const [temperature, setTemperature] = useState<number | null>(0.2);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [tests, prompts] = await Promise.all([
        evaluationApi.listEvaluationTests(false),
        evaluationApi.listSystemPrompts(false),
      ]);

      setSourceTests(tests);
      setSystemPrompts(prompts);
      setRunName(generateDefaultName());
    } catch (err: any) {
      console.error('Failed to load data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  // Filter to only AIExecutionScorer prompts
  const scoringPrompts = systemPrompts.filter(p =>
    p.agentType?.toLowerCase() === 'aiexecutionscorer'
  );

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!runName.trim()) {
      errors.runName = 'Run name is required';
    }

    if (!sourceTestId) {
      errors.sourceTestId = 'Source evaluation test is required';
    }

    if (!systemPromptHash) {
      errors.systemPromptHash = 'System prompt is required';
    }

    if (!judgeModelProvider) {
      errors.judgeModelProvider = 'Judge model provider is required';
    }

    if (!judgeModelId) {
      errors.judgeModelId = 'Judge model is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const runId = await evaluationApi.createScoringValidationRun({
        name: runName.trim(),
        description: description.trim() || undefined,
        sourceTestId,
        systemPromptHash,
        judgeModelProvider,
        judgeModelId,
        temperature: temperature ?? undefined,
      });

      await evaluationApi.startScoringValidationRun(runId);

      navigate(`/opinions/scoring/${runId}`);
    } catch (err: any) {
      console.error('Failed to create validation run:', err);
      setError(err.message || 'Failed to create validation run');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    navigate('/opinions/scoring');
  };

  const truncateHash = (hash: string) => {
    return hash.length > 12 ? `${hash.substring(0, 8)}...${hash.substring(hash.length - 4)}` : hash;
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div data-component="CreateScoringValidationPage" className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            <p className="text-gray-500">Loading...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      pageTitle="New Scoring Validation Run"
      pageDescription="Configure and start a new scoring validation run"
    >
      <div data-component="CreateScoringValidationPage" className="flex-1 overflow-y-auto p-6">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Scoring Validation Run</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Re-score evaluation executions with a custom judge model
                </p>
              </div>
              <button
                onClick={handleClose}
                disabled={isSubmitting}
                className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Error Messages */}
          {error && (
            <div data-flow="error" className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-red-800">{error}</p>
              </div>
            </div>
          )}

          {Object.keys(validationErrors).length > 0 && (
            <div data-flow="validation-errors" className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-yellow-800 font-medium mb-2">Please fix the following errors:</p>
              <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1">
                {Object.values(validationErrors).map((err, index) => (
                  <li key={index}>{err}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="runName" className="block text-sm font-medium text-gray-700 mb-1">
                    Run Name *
                  </label>
                  <input
                    id="runName"
                    type="text"
                    value={runName}
                    onChange={(e) => setRunName(e.target.value)}
                    disabled={isSubmitting}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 ${
                      validationErrors.runName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {validationErrors.runName && (
                    <p className="text-sm text-red-600 mt-1">{validationErrors.runName}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description (optional)
                  </label>
                  <textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={isSubmitting}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                    placeholder="Describe the purpose of this validation run"
                  />
                </div>
              </div>
            </div>

            {/* Source Test Selection */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Source Evaluation Test</h3>
              <div>
                <label htmlFor="sourceTest" className="block text-sm font-medium text-gray-700 mb-1">
                  Select Evaluation Test *
                </label>
                <select
                  id="sourceTest"
                  value={sourceTestId}
                  onChange={(e) => setSourceTestId(e.target.value)}
                  disabled={isSubmitting}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 ${
                    validationErrors.sourceTestId ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select a test...</option>
                  {sourceTests.map((test) => (
                    <option key={test.id} value={test.id}>
                      {test.name} ({test.currentExecutionCount} executions)
                    </option>
                  ))}
                </select>
                {validationErrors.sourceTestId && (
                  <p className="text-sm text-red-600 mt-1">{validationErrors.sourceTestId}</p>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  The executions from this test will be re-scored
                </p>
              </div>
            </div>

            {/* System Prompt Selection */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Scoring System Prompt</h3>
              <div>
                <label htmlFor="systemPrompt" className="block text-sm font-medium text-gray-700 mb-1">
                  Select System Prompt *
                </label>
                <select
                  id="systemPrompt"
                  value={systemPromptHash}
                  onChange={(e) => setSystemPromptHash(e.target.value)}
                  disabled={isSubmitting}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 ${
                    validationErrors.systemPromptHash ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select a prompt...</option>
                  {scoringPrompts.map((prompt) => (
                    <option key={prompt.hash} value={prompt.hash}>
                      {prompt.description || 'No description'} ({new Date(prompt.createdAt).toLocaleString()}) [{truncateHash(prompt.hash)}]
                    </option>
                  ))}
                </select>
                {validationErrors.systemPromptHash && (
                  <p className="text-sm text-red-600 mt-1">{validationErrors.systemPromptHash}</p>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  The system prompt used by the judge model for scoring
                </p>
              </div>
            </div>

            {/* Judge Model Configuration */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Judge Model Configuration</h3>
              <div className="space-y-4">
                <JudgeModelSelector
                  providerType={judgeModelProvider}
                  modelId={judgeModelId}
                  onChange={({ providerType, modelId, modelInfo }) => {
                    setJudgeModelProvider(providerType);
                    setJudgeModelId(modelId);
                    setJudgeModelInfo(modelInfo);
                    if (modelInfo && !modelInfo.isTemperatureAllowed) {
                      setTemperature(null);
                    } else if (modelInfo && temperature === null) {
                      setTemperature(modelInfo.defaultTemperature);
                    }
                  }}
                  disabled={isSubmitting}
                  providerError={validationErrors.judgeModelProvider}
                  modelError={validationErrors.judgeModelId}
                />

                {judgeModelInfo?.isTemperatureAllowed !== false && (
                <div>
                  <label htmlFor="temperature" className="block text-sm font-medium text-gray-700 mb-1">
                    Temperature
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      id="temperature"
                      type="number"
                      value={temperature ?? 0}
                      onChange={(e) => setTemperature(parseFloat(e.target.value) || 0)}
                      disabled={isSubmitting}
                      min={judgeModelInfo?.minTemperature ?? 0}
                      max={judgeModelInfo?.maxTemperature ?? 1}
                      step={0.1}
                      className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                    />
                    <span className="text-sm text-gray-500">
                      Range: {judgeModelInfo?.minTemperature ?? 0} - {judgeModelInfo?.maxTemperature ?? 1}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Higher values produce more diverse outputs, lower values are more focused
                  </p>
                </div>
                )}
                {judgeModelInfo?.isTemperatureAllowed === false && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <p className="text-sm text-gray-600">
                      Temperature is not configurable for this model — the model uses its default value.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="pt-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Start Validation Run'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AppLayout>
  );
};